/**
 * Handler dos pedidos `get` a API.
 * Conforme a rota em que o utilizador se encontra é realizado
 * um pedido a API de forma a obter a informação dessa página.
 * Implementado validações de permissões e de pedidos.
 *
 * @since 1.0.0
 * @category Hook
 * @returns {Object}
 *
 */

// packages
import { reactive, toRefs, watch } from '@vue/composition-api';

// helpers
import { get } from '@/helpers/requests';

// utils
import { haveProperty } from '@/utils/';

// store
import { mapState } from '@/hooks/use-store';

// instance
import getRuntimeVM from '@/utils/runtime';

const response = mapState({ module: 'storePayload', key: 'response' });

const useModels = () => {
    // obtém as routes;
    const vm = getRuntimeVM();
    let route = vm.$route;
    let isAddPage = false;
    let previousRouteIsAddPage = false;

    // definção do state
    const state = reactive({
        loading: false,
        error: false,
        data: {}
    });

    // reset do state
    const resetState = ({ newData = {}, newIsEdition }) => {
        state.data = {
            model: { ...newData },
            isEdition: newIsEdition,
            permissions: {
                allowFilling: true
            }
        };
    };

    // Se falhar alguma guard, significa que
    // não pode fazer o pedido a API.
    const guards = {
        // Se existir uma resposta na store com a key `content`,
        // e se a anterior página é de adição ou se é necessário fazer update
        // ao modelo.
        // Como tal, é desnecessário fazero pedido a API, dado que esse novo
        // modelo esta presente na store.previousRouteIsAddPage
        previousPageIsAdd: updateModel => {
            if ( !haveProperty( response.value, 'content' )
                || !( ( previousRouteIsAddPage && !Array.isArray( response.value.content ) ) || updateModel ) )
                return false;

            resetState({ newData: response.value.content, newIsEdition: true });
            return true;
        },
        // se for uma página para adicionar:
        // -- não executa o pedido a API.
        // -- reset do state
        // -- toggle da key `isEdition` para false
        // -- caso anteriormente seja uma página de edição
        //    é feito o reset dos campos anteriormente preenchidos.
        isAddPage: () => {
            if ( !isAddPage )
                return false;

            resetState({ newIsEdition: false });
            return true;
        },
        pagesWithoutRequest: () => {
            const pages = [ 'profile', 'notFound' ];

            if ( !pages.includes( route.name ) )
                return false;

            return true;
        }
    };

    const fetch = async ({ updateModel = false }) => {
        // validação de guards.
        if (
            guards.previousPageIsAdd( updateModel )
            || guards.isAddPage()
            || guards.pagesWithoutRequest()
        )
            return;

        const { specificEndpoint } = route.meta;

        const endpoint = specificEndpoint
            ? `${specificEndpoint}/${route.params.id || ''}`
            : route.path;

        state.loading = true;

        // validação do tipo de página
        const wasListPage = !haveProperty( route.meta, 'api' );

        const haveError = await get(
            endpoint,
            { ...( wasListPage && { paginate: 20 }), ...getQueryParams( route.query ) }
        )
            .then( ({ data }) => {
                const key = wasListPage ? 'list' : 'model';
                state.data = {
                    [key]: data.data,
                    ...( wasListPage && { pagination: data.pagination }),
                    ...( !wasListPage && {
                        isEdition: !isAddPage,
                        permissions: {
                            allowFilling: true
                        }
                    })
                };

                state.loading = false;
                return false;
            })
            .catch( () => {
                state.loading = false;
                return false;
            });

        state.loading = haveError;
    };

    /**
     *
     * Função que prepara a query para ser feito o pedido à API.
     * Quando há parâmetros de pesquisa que são arrays mas quando
     * tem apenas uma opção do array selecionada, ao atualizar a página,
     * vai fazer o pedido como se fosse uma string, então tem de ser feito
     * um tratamento prévio da query para dar conta desta situação.
     *
     * @param {*} query
     * @return finalQuery
     *
     */
    const getQueryParams = query => {
        /* eslint-disable */
        const {
            search,
            leavingReason,
            missingDate,
            type,
            state,
            page,
            sort,
            direction,
            ...otherParams
        } = query;
        /* eslint-enable */

        const finalQuery = {
            ...( search && { search }),
            ...( leavingReason && { leavingReason }),
            ...( missingDate && { missingDate }),
            ...( type && { type }),
            ...( state && { state }),
            ...( page && { page }),
            ...( sort && { sort }),
            ...( direction && { direction })
        };

        Object.keys( otherParams ).forEach( param => {
            finalQuery[param] = ( !Array.isArray( otherParams[param] ) )
                ? [ otherParams[param] ]
                : otherParams[param];
        });

        return finalQuery;
    };

    // listener para actualizar as rotas, conforme
    // o utilizador navega no software.
    watch( () => vm.$route,
        ( newRoute, previousRoute ) => {
            previousRouteIsAddPage = previousRoute?.meta.isAdd;
            state.data = {};
            route = newRoute;
            isAddPage = newRoute.meta.isAdd;
            fetch({});
        }, {
            immediate: true
        });

    return { ...toRefs( state ), fetch };
};

export default useModels;